import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './GANFlower.css';

function GANFlower() {
  const navigate = useNavigate();
  const [generatedImage, setGeneratedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);

  const API_BASE = 'https://imdb-sentiment-backend.onrender.com/api/gan';

  const handleGenerate = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const blob = await response.blob();
        const imageUrl = URL.createObjectURL(blob);
        setGeneratedImage(imageUrl);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to generate flower');
      }
    } catch (err) {
      setError('Failed to connect to server. The model may be loading...');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!generatedImage) return;

    const link = document.createElement('a');
    link.href = generatedImage;
    link.download = `flower_${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            <h2>How This AI Works</h2>

            <div className="explanation-section">
              <h3>Technical Explanation</h3>
              <p>
                <strong>Step 1:</strong> Download the Flowers102 dataset containing 8,189 high-resolution images across 102 flower species.
                <strong> Step 2:</strong> Resize images to 64×64 pixels and normalize pixel values to [-1, 1] range.
                <strong> Step 3:</strong> Build two competing networks - a Generator (creates fake flower images from 100-dimensional random noise using transposed convolutions, batch normalization, and LeakyReLU) and a Discriminator (classifies images as real or fake using strided convolutions).
                <strong> Step 4:</strong> Train adversarially for 400 epochs: the Discriminator learns to distinguish real flowers from generated ones, while the Generator learns to fool the Discriminator.
                <strong> Step 5:</strong> Use binary cross-entropy loss for both networks and Adam optimizer with learning rate 0.0002 and beta1=0.5.
                <strong> Step 6:</strong> Monitor training through loss curves and generated image quality at milestone epochs.
                <strong> Step 7:</strong> Save the trained Generator weights.
                <strong> Step 8:</strong> Deploy via Flask API where random noise vectors are passed through the Generator to create unique synthetic flower images.
              </p>
            </div>

            <div className="explanation-section simply-put">
              <h3>Simply Put</h3>
              <p>
                The AI learned to create new flowers by studying 8,000 real flower photos. Imagine two artists: one tries to paint fake flowers that look real, and the other tries to spot which flowers are fake. They compete for 400 rounds of practice. The faker gets better and better at drawing realistic petals, colors, and stems, while the detective gets better at spotting fakes. Eventually, the faker becomes so skilled that it can create beautiful, brand-new flowers that never existed in nature - each one completely unique!
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="gan-page">
        <div className="gan-container">
          <button onClick={() => navigate('/')} className="back-button">
            ← Back to Portfolio
          </button>
          <button className="info-btn" onClick={() => setShowModal(true)}>
            ℹ️ How It Works
          </button>

          <header className="gan-header">
            <h1>GAN Flower Generator</h1>
            <p className="subtitle">Generate realistic flower images using Generative Adversarial Networks</p>
          </header>

        <div className="gan-content">
          <div className="info-section">
            <h2>About This Project</h2>
            <p>
              This application uses a Deep Convolutional GAN (DCGAN) trained on the Flowers102 dataset
              to generate synthetic flower images from random noise vectors. Each click creates a unique,
              AI-generated flower that has never existed before.
            </p>
            <div className="model-specs">
              <h3>Model Architecture</h3>
              <ul>
                <li><strong>Model Type:</strong> Deep Convolutional GAN (DCGAN)</li>
                <li><strong>Training Dataset:</strong> Flowers102 (8,189 images, 102 species)</li>
                <li><strong>Generator Parameters:</strong> 3.58 million</li>
                <li><strong>Output Resolution:</strong> 64×64 RGB images</li>
                <li><strong>Training Epochs:</strong> 400</li>
              </ul>
            </div>
          </div>

          <div className="generator-section">
            <div className="generation-controls">
              <button
                onClick={handleGenerate}
                disabled={loading}
                className="generate-button"
              >
                {loading ? 'Generating...' : 'Generate Flower'}
              </button>

              {generatedImage && (
                <button
                  onClick={handleDownload}
                  className="download-button"
                >
                  Download Image
                </button>
              )}
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <div className="image-display">
              {loading && (
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <p>Generating your unique flower...</p>
                </div>
              )}

              {generatedImage && !loading && (
                <div className="generated-image-container">
                  <img
                    src={generatedImage}
                    alt="Generated flower"
                    className="generated-image"
                  />
                  <p className="image-caption">
                    Synthetic flower generated by DCGAN
                  </p>
                </div>
              )}

              {!generatedImage && !loading && (
                <div className="placeholder">
                  <p>Click "Generate Flower" to create a unique AI-generated flower image</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="tech-details">
          <h3>Technical Details</h3>
          <div className="details-grid">
            <div className="detail-card">
              <h4>Generator Network</h4>
              <p>Transforms 100-dimensional noise vectors into 64×64 RGB images through 5 upsampling layers with batch normalization and LeakyReLU activations.</p>
            </div>
            <div className="detail-card">
              <h4>Adversarial Training</h4>
              <p>Trained against a discriminator network for 400 epochs, learning to generate realistic flower structures, colors, and textures.</p>
            </div>
            <div className="detail-card">
              <h4>Applications</h4>
              <p>Demonstrates synthetic image generation capabilities relevant to understanding deepfakes and AI-generated content on social media.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

export default GANFlower;
